// client/src/components/recursos/RecursoRow.js - VERSÃO COMPLETA COM SUPORTE A POOL
import React from 'react';
import { MoreVertical, Home, Box, Car, CheckCircle, XCircle, Wrench, Ban, Image as ImageIcon, Folder, Layers, Users } from 'lucide-react';
import PropTypes from 'prop-types';

const RecursoIcon = ({ tipo, size = 16 }) => {
    switch (tipo) {
        case 'Sala': return <Home size={size} />;
        case 'Equipamento': return <Box size={size} />;
        case 'Maca ou Cadeira': return <Box size={size} />;
        case 'Veiculo': return <Car size={size} />;
        case 'Outros': return <Box size={size} />;
        default: return <Box size={size} />;
    }
};

const StatusBadge = ({ status }) => {
    const statusConfig = {
        'Ativo': { color: 'bg-green-100 text-green-800', icon: CheckCircle },
        'Inativo': { color: 'bg-red-100 text-red-800', icon: XCircle },
        'Em Manutenção': { color: 'bg-yellow-100 text-yellow-800', icon: Wrench },
        'Bloqueado': { color: 'bg-orange-100 text-orange-800', icon: Ban },
        'Fora de Serviço': { color: 'bg-gray-100 text-gray-800', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig['Inativo'];
    const IconComponent = config.icon;

    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <IconComponent size={12} className="mr-1" />
            {status}
        </span>
    );
};

const CategoriaBadge = ({ categoria }) => {
    let nomeCategoria = 'Sem categoria';
    let tipoCategoria = '';
    
    if (categoria) {
        if (typeof categoria === 'string') {
            nomeCategoria = 'Carregando...';
        } else if (categoria.nome) {
            nomeCategoria = categoria.nome;
            tipoCategoria = categoria.tipo ? `(${categoria.tipo})` : '';
        } else if (categoria._id) {
            nomeCategoria = 'Categoria ID: ' + categoria._id.substring(0, 8) + '...';
        }
    }

    return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
            <Folder size={12} className="mr-1" />
            {nomeCategoria} {tipoCategoria}
        </span>
    );
};

// ✅ NOVO: Badge para mostrar se é um pool de recursos
const PoolBadge = ({ recurso }) => {
    if (recurso.isGrupoDeRecursos) {
        const quantidade = recurso.recursosDoGrupo?.length || 0;
        return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                <Layers size={12} className="mr-1" />
                Pool ({quantidade} recursos)
            </span>
        );
    }
    
    if (recurso.grupoPertencente) {
        return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                <Users size={12} className="mr-1" />
                Membro de Pool
            </span>
        );
    }
    
    return null;
};

const ImagensBadge = ({ recurso }) => {
    const totalImagens = (recurso.imagens?.length || 0) + (recurso.imagensDaGaleria?.length || 0);
    
    if (totalImagens === 0) {
        return null;
    }

    return (
        <span className="inline-flex items-center ml-2 px-1.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
            <ImageIcon size={10} className="mr-1" />
            {totalImagens}
        </span>
    );
};

const RecursoRow = ({ recurso, onOpenActions, loading }) => {
    const recursoId = recurso._id || recurso.id;

    return (
        <tr className="table-row-hover">
            <td className="td-name">
                <div className="flex items-center gap-2">
                    <span className="table-icon-wrapper">
                        <RecursoIcon tipo={recurso.tipo} />
                    </span>
                    <div className="flex flex-col">
                        <div className="flex items-center flex-wrap gap-1">
                            <span className="font-medium">{recurso.nome}</span>
                            <ImagensBadge recurso={recurso} />
                        </div>
                        {recurso.descricao && (
                            <span className="text-xs text-gray-500 truncate max-w-xs">
                                {recurso.descricao}
                            </span>
                        )}
                    </div>
                </div>
            </td>
            <td>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {recurso.tipo}
                </span>
            </td>
            <td>
                <CategoriaBadge categoria={recurso.categoria} />
            </td>
            <td>
                <StatusBadge status={recurso.status} />
            </td>
            <td>
                <PoolBadge recurso={recurso} />
            </td>
            <td className="text-right">
                <button
                    onClick={() => onOpenActions(recurso)}
                    title="Ações"
                    className="action-button-icon text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading || !recursoId}
                >
                    <MoreVertical size={18} />
                </button>
            </td>
        </tr>
    );
};

RecursoRow.propTypes = {
    recurso: PropTypes.shape({
        _id: PropTypes.string,
        id: PropTypes.string,
        nome: PropTypes.string,
        tipo: PropTypes.string,
        descricao: PropTypes.string,
        categoria: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({
                _id: PropTypes.string,
                nome: PropTypes.string,
                tipo: PropTypes.string
            })
        ]),
        status: PropTypes.string,
        imagens: PropTypes.array,
        imagensDaGaleria: PropTypes.array,
        isGrupoDeRecursos: PropTypes.bool,
        recursosDoGrupo: PropTypes.array,
        grupoPertencente: PropTypes.string
    }).isRequired,
    onOpenActions: PropTypes.func.isRequired,
    loading: PropTypes.bool
};

export default RecursoRow;